import React, { useMemo, useState } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, Alert, Tooltip } from '@mui/material';
import { Menu as MenuIcon, DarkModeOutlined, LightModeOutlined, ContrastOutlined, SettingsAccessibility } from '@mui/icons-material';
import { Outlet, Link } from 'react-router-dom';
import { DrawerContent } from '../components/layout/DrawerContent';
import { GlobalSearchBar } from '../components/layout/GlobalSearchBar';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { useAuth } from '@/hooks/useAuth';
import { AuthEventHandler } from '@/components/auth/AuthEventHandler';
import { getEnvironmentBanner, getReleaseVersion } from '@/utils/environment';
import { useMenuPrefsStore } from '@/stores/useMenuPrefsStore';
import { useThemePrefsStore, type ThemeMode } from '@/stores/useThemePrefsStore';
import { useActivityHeartbeat } from '@/hooks/useActivityHeartbeat';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { SkipNavigation, RouteAnnouncer, AccessibilityDrawer } from '@/components/accessibility';
import { useRouteFocusReset } from '@/hooks/useRouteFocusReset';
import { useSyncUserPreferences } from '@/hooks/useSyncUserPreferences';
import { DegradedHealthBanner } from '@/components/shared/DegradedHealthBanner';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';

const ENV_BANNER = getEnvironmentBanner();
const RELEASE_VERSION = getReleaseVersion();
const ENV_BANNER_HEIGHT = 22;

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

const THEME_MODE_CONFIG: Record<ThemeMode, { icon: React.ReactNode; label: string }> = {
  light: { icon: <LightModeOutlined />, label: 'Theme: Light — click for Dark' },
  dark: { icon: <DarkModeOutlined />, label: 'Theme: Dark — click for System' },
  system: { icon: <ContrastOutlined />, label: 'Theme: System — click for Light' },
};

export const MainLayout: React.FC = () => {
  const { isAuthenticated, hasRoleConflict, isViewer, isFrontWindowOnly } = useAuth();
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const conflictingRolesMessage = iemConfig.features.front_window.conflicting_roles_message;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [a11yDrawerOpen, setA11yDrawerOpen] = useState(false);
  useActivityHeartbeat();
  useTokenRefresh();
  useRouteFocusReset();
  useSyncUserPreferences();
  const { sidebarCollapsed, setSidebarCollapsed } = useMenuPrefsStore();
  const { themeMode, cycleThemeMode } = useThemePrefsStore();

  const drawerWidth = useMemo(
    () => (isAuthenticated ? (sidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH) : 0),
    [isAuthenticated, sidebarCollapsed],
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleCollapseToggle = () => setSidebarCollapsed(!sidebarCollapsed);

  const drawerContent = <DrawerContent collapsed={sidebarCollapsed} onCollapseToggle={handleCollapseToggle} />;

  // Desktop drawer needs spacers for the full-width AppBar + optional env banner
  const desktopDrawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {drawerContent}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <SkipNavigation />
      <RouteAnnouncer />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          ml: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {/* Logo — centered above the sidebar within the app bar */}
          <Box
            component={Link}
            to="/"
            sx={{
              position: 'absolute',
              left: 0,
              width: { xs: 'auto', md: EXPANDED_WIDTH },
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'center' },
              textDecoration: 'none',
              height: '100%',
              px: 2,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={`/logo.png?v=${__GIT_COMMIT__}`}
              alt="eQuip"
              sx={{ height: 48, width: 'auto', filter: 'brightness(2.5)', py: '2px' }}
            />
          </Box>
          {/* Spacer to push right-side controls to the end */}
          <Box sx={{ flexGrow: 1 }} />
          {/* Search bar: absolutely centered on the full viewport */}
          {isAuthenticated && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                width: { xs: 260, sm: 400, md: 520, lg: 640 },
                maxWidth: 'calc(100vw - 200px)',
              }}
            >
              <GlobalSearchBar />
            </Box>
          )}
          {!isAuthenticated && (
            <Typography
              variant="h4"
              noWrap
              component="div"
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                fontWeight: 'medium',
                letterSpacing: '0.25px',
                color: 'white',
              }}
            >
              eQuip Asset Management System
            </Typography>
          )}
          <Tooltip title="Accessibility settings">
            <IconButton color="inherit" onClick={() => setA11yDrawerOpen((prev) => !prev)} aria-label="Open accessibility settings" sx={{ ml: 1 }}>
              <SettingsAccessibility />
            </IconButton>
          </Tooltip>
          <Tooltip title={THEME_MODE_CONFIG[themeMode].label}>
            <IconButton color="inherit" onClick={cycleThemeMode} aria-label="toggle theme" sx={{ ml: 1 }}>
              {THEME_MODE_CONFIG[themeMode].icon}
            </IconButton>
          </Tooltip>
          <DateTimeDisplay textColor="common.white" sx={{ ml: 2 }} />
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer - only show when authenticated */}
      {isAuthenticated && (
        <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: EXPANDED_WIDTH,
              },
            }}
          >
            {drawerContent}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                overflowX: 'hidden',
                transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
              },
            }}
            open
          >
            {desktopDrawerContent}
          </Drawer>
        </Box>
      )}

      {/* Main content */}
      <Box
        id="main-content"
        tabIndex={-1}
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          width: isAuthenticated ? { md: `calc(100% - ${drawerWidth}px)` } : '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default,
          outline: 'none', // suppress focus ring on the landmark itself
        })}
      >
        <Toolbar /> {/* Spacer for fixed AppBar */}
        {ENV_BANNER && (
          <Box
            sx={{
              width: '100%',
              height: ENV_BANNER_HEIGHT,
              bgcolor: ENV_BANNER.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: ENV_BANNER.textColor,
                fontWeight: 900,
                letterSpacing: '0.15em',
                fontSize: '0.7rem',
              }}
            >
              {RELEASE_VERSION ? `{ ${RELEASE_VERSION} }  ` : ''}{ENV_BANNER.label} ENVIRONMENT{RELEASE_VERSION ? `  { ${RELEASE_VERSION} }` : ''}
            </Typography>
          </Box>
        )}
        {hasRoleConflict && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {isViewer
              ? 'Visitor Access Only — Your account has conflicting roles. You have been granted read-only visitor access.'
              : isFrontWindowOnly
                ? conflictingRolesMessage
                : 'Your account has conflicting roles. Some features may be restricted.'}
          </Alert>
        )}
        {isAuthenticated && <DegradedHealthBanner />}
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </Box>
      </Box>

      {/* Global auth event handler - must be inside Router context */}
      <AuthEventHandler />

      {/* Accessibility settings drawer */}
      <AccessibilityDrawer open={a11yDrawerOpen} onClose={() => setA11yDrawerOpen(false)} />
    </Box>
  );
};
